import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from 'react-scale/components/design-system/styles';
import Alert from 'design-system/Alert';
import { sampleTask } from '@scale/shared/selfServe/validationHelpers';
import TaskUITab from 'react-scale/dashboard.scale.com/components/SelfServe/Taxonomy/TaskUITab';
import SAlert from 'react-s-alert';
import { Button, H2 } from 'react-scale/components/design-system';
import { useMutation } from 'react-query';
import { validateTaskTemplate } from 'react-scale/corp/lib/api/taskTemplate';
import SanitizedMarkdown from 'react-scale/components/design-system/SanitizedMarkdown';
import { Text, Spinner, TextInput } from 'design-system';
import UISelect from 'corp/components/UI/Select';
import { BEE_TTS_LOCALES, BEE_TTS_PROTOCOLS } from '@scale/shared/textcollection/beeTTSHelpers';
import apiFetch from 'react-scale/lib/api-fetch';
import { ISubtaskJson } from '@scale/shared/types/subtask';
import FieldsBuilder from '../QM/taskTemplatesEditor/FieldsBuilder';
import {
  TextCollectionField,
  TextCollectionParams,
} from '@scale/shared/types/params/textcollection';
import { typeBuilderMap } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemas';

// Adapted from frontend/dashboard.scale.com/components/SelfServe/Taxonomy/index.tsx

const description = `
### Description:
- This tool allows you to create a new template task based on an existing one
1. Select the protocol and locale to clone.
2. Edit the json for the cloned project
3. Select the protocol and locale for the new template task
4. Click on Create button

##### Author: Carlos Gonzalez
---
`;

const taskPropertiesForDisplay = {
  timerDisabled: true,
  shouldWarnOnExit: false,
  readOnly: false,
  editable: true,
  canEdit: true,
  isCustomer: true,
  disableShortcuts: true,
  showHeader: false,
};

enum TemplatesTab {
  Editor = 'editor',
  Preview = 'preview',
}

const EditJsonTemplateTask: React.FC<{ templateTask: ISubtaskJson }> = ({ templateTask }) => {
  const classes = useStyles();
  const initParams = templateTask.params;

  const [params, setParams] = useState<any>(initParams);
  const [loading, setLoading] = useState<boolean>(false);
  const [taskTab, setTaskTab] = useState<TemplatesTab>(TemplatesTab.Editor);
  const [task, setTask] = useState({
    ...sampleTask,
    type: templateTask.type,
    params,
    assignment: {
      projectId: templateTask.project,
      params,
      type: templateTask.type,
    },
    ...taskPropertiesForDisplay,
  });
  const [targetProtocol, setTargetProtocol] = useState<string>();
  const [targetLocale, setTargetLocale] = useState<string>();

  const validateTaskTemplateMutation = useMutation(templates => {
    return validateTaskTemplate(templateTask.type, templates);
  });

  const onSetTab = (tab: TemplatesTab) => {
    if (tab === TemplatesTab.Preview) {
      validateTaskTemplateMutation.mutate(templateTask.params as any);
    }
    setTaskTab(tab);
  };

  const updateTask = useCallback(async () => {
    try {
      setTask(currentTask => ({
        ...currentTask,
        params,
        assignment: {
          projectId: templateTask.project,
          params,
          type: templateTask.type,
        },
      }));
    } catch (error) {
      // do nothing for now
      SAlert.error(JSON.stringify(error));
    }
  }, [params, templateTask.project, templateTask.type]);

  useEffect(() => {
    updateTask();
  }, [params, updateTask]);

  useEffect(() => {
    setTargetProtocol(templateTask.params.metadata.protocol);
  }, [templateTask.params.metadata.protocol]);

  const updateParams = useCallback(async (callback: (arg: any) => any) => {
    setParams(callback);
  }, []);

  const setFields = useCallback(
    (fields: TextCollectionField[]) => {
      updateParams((currentParams: TextCollectionParams) => {
        return { ...currentParams, fields };
      });
    },
    [updateParams],
  );

  const setInstruction = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      updateParams((currentParams: TextCollectionParams) => {
        return { ...currentParams, instruction: e.target.value };
      });
    },
    [updateParams],
  );

  const saveTemplate = async () => {
    setLoading(true);
    try {
      const { message, success } = await apiFetch(null, `corp-api/bee-tts-utils/createTemplate`, {
        method: 'POST',
        body: {
          params,
          protocol: targetProtocol,
          locale: targetLocale,
        },
      });
      if (success) {
        SAlert.success(message);
      } else {
        SAlert.error(message);
      }
    } catch (err) {
      SAlert.error(err?.message || err);
    }
    setLoading(false);
  };

  const tabs = [
    { label: 'Editor', value: TemplatesTab.Editor },
    { label: 'Preview', value: TemplatesTab.Preview },
  ];

  return (
    <>
      {validateTaskTemplateMutation.isSuccess && !validateTaskTemplateMutation.data?.valid && (
        <Alert severity="error" style={{ marginTop: '16px' }}>
          {validateTaskTemplateMutation.data?.message}
        </Alert>
      )}
      <div className={classes.header}>
        {tabs.map(tab => {
          return (
            <div
              key={tab.value}
              onClick={() => {
                onSetTab(tab.value);
              }}
              className={taskTab === tab.value ? classes.activeTablink : classes.tablink}
            >
              {tab.label}
            </div>
          );
        })}
      </div>
      {taskTab === TemplatesTab.Editor && (
        <div className={classes.editor}>
          <div className={classes.builder}>
            <div className={classes.builderTitle}>
              <H2>Instruction</H2>
            </div>
            <TextInput
              multiline
              classes={{ root: classes.textarea }}
              placeholder="Set the instruction for the template task"
              defaultValue={params.instruction}
              onChange={setInstruction}
            />
          </div>
          <div className={classes.builder}>
            <div className={classes.builderTitle}>
              <H2>Fields</H2>
            </div>
            <FieldsBuilder
              typeBuilderMap={typeBuilderMap}
              name={''}
              existingValue={params.fields}
              updateProperty={setFields}
            />
          </div>
        </div>
      )}
      {taskTab === TemplatesTab.Preview &&
        validateTaskTemplateMutation.isSuccess &&
        validateTaskTemplateMutation.data?.valid && (
          <TaskUITab taskProps={{ ...task, isTaxonomyDisplay: true }} />
        )}
      <div className={classes.bottom}></div>
      <section className={classes.container}>
        <div>
          <Text medium>Target Protocol</Text>
        </div>
        <UISelect
          value={targetProtocol}
          onChange={(event: { value: string }) => setTargetProtocol(event?.value || '')}
          placeholder="Select a target protocol"
          options={Object.entries(BEE_TTS_PROTOCOLS).map(([_protocol, value]) => ({
            value,
            label: value,
          }))}
        />
      </section>
      <section className={classes.container}>
        <div>
          <Text medium>Target Locale</Text>
        </div>
        <UISelect
          value={targetLocale}
          onChange={(event: { value: string }) => setTargetLocale(event?.value || '')}
          placeholder="Select a target locale"
          options={Object.entries(BEE_TTS_LOCALES).map(([_locale, value]) => ({
            value,
            label: value,
          }))}
        />
      </section>
      <Button
        onClick={saveTemplate}
        disabled={loading || !targetProtocol || !targetLocale}
        type={'primary'}
        className={classes.saveButton}
      >
        Save Template
      </Button>
    </>
  );
};

const useStyles = makeStyles({
  header: {
    display: 'flex',
    fontSize: '16px',
    fontWeight: 500,
    marginBottom: '32px',
    marginTop: '12px',
    borderBottom: '1px solid var(--color-RemoGray10)',
  },
  tablink: {
    cursor: 'pointer',
    padding: '12px 36px',
    color: 'var(--color-RemoGray50)',

    '&:hover': {
      color: 'var(--color-RemoGray75)',
    },
  },
  activeTablink: {
    color: 'black',
    padding: '12px 36px',
    borderBottom: '1px solid var(--color-pink)',
  },
  extra: {
    display: 'flex',
    marginLeft: 'auto',
  },
  text: {
    opacity: 0.5,
  },
  bottom: {
    borderBottom: '1px solid var(--bg-gray-light-1)',
    marginBottom: '50px',
  },
  sideblock: {
    display: 'inline-block',
    marginLeft: '80px',
  },
  sideblockText: {
    maxWidth: 400,
    marginBottom: 10,
  },
  sideblockButton: {
    marginBottom: 10,
  },
  editor: {
    display: 'flex',
    flexDirection: 'column',
  },
  saveButton: {
    marginLeft: 'auto',
  },
  errorText: {
    color: 'red',
  },
  container: {
    margin: '16px 0',
    width: '50%',
  },
  builder: {
    marginBottom: 20,
  },
  builderTitle: {
    marginBottom: 10,
  },
  textarea: {
    width: '100%',
  },
});

const CreateTemplateTask: React.FC = () => {
  const [protocol, setProtocol] = useState<string>();
  const [locale, setLocale] = useState<string>();
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [template, setTemplate] = useState<any>(false);

  const validateTemplateTask = useCallback(async () => {
    setLoadingTemplate(true);
    const res = await apiFetch(null, 'corp-api/bee-tts-utils/validateTemplate', {
      method: 'POST',
      body: { protocol, locale },
    });

    if (res.template) {
      res.template.batchName = res.batchName;
    }

    setTemplate(res.template);
    setLoadingTemplate(false);
  }, [locale, protocol]);

  useEffect(() => {
    if (protocol && locale) {
      validateTemplateTask();
    }
  }, [protocol, locale, validateTemplateTask]);

  return (
    <div className="root">
      <section>
        <SanitizedMarkdown text={description} />
      </section>

      <section className="container">
        <div className="textTitle">
          <Text medium>Protocol to Clone</Text>
        </div>
        <UISelect
          className="input-component"
          value={protocol}
          onChange={(event: { value: string }) => setProtocol(event?.value || '')}
          placeholder="Select a protocol"
          options={Object.entries(BEE_TTS_PROTOCOLS).map(([_protocol, value]) => ({
            value,
            label: value,
          }))}
        />
      </section>
      <section className="container">
        <div className="textTitle">
          <Text medium>Locale to Clone</Text>
        </div>
        <UISelect
          className="input-component"
          value={locale}
          onChange={(event: { value: string }) => setLocale(event?.value || '')}
          placeholder="Select a locale"
          options={Object.entries(BEE_TTS_LOCALES).map(([_locale, value]) => ({
            value,
            label: value,
          }))}
        />
        {protocol && locale && !loadingTemplate && !template && (
          <p className="template-validation">❌ Template task not found</p>
        )}
      </section>
      {loadingTemplate && <Spinner style={{ paddingBottom: '16px' }} />}
      {!!template && !loadingTemplate && (
        <>
          <section>
            <EditJsonTemplateTask templateTask={template} />
          </section>
        </>
      )}
      <style jsx>
        {`
          .container {
            margin: 16px 0;
            width: 50%;
          }
          .template-validation {
            margin: 0;
          }
          .disabled {
            color: var(--color-RemoGray50);
          }
          .file-validation {
            display: flex;
            justify-content: space-between;
          }
          .input-component {
            width: 75%;
          }
          .loader {
            height: 2px;
            width: 2px;
          }
        `}
      </style>
    </div>
  );
};

export default CreateTemplateTask;
